import httpclient from 'libs/Axios';

export type SeoPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface SeoOptimizationItem {
    issue: string;
    action: string;
    effort: string;
    impact: string;
    category: string;
    priority: SeoPriority | string;
}

export interface SeoReport {
    url?: string;
    timestamp?: string;
    time_stamp?: string;
    total_issues?: number;
    optimization_plan?: SeoOptimizationItem[];
    issues_by_priority?: Partial<Record<SeoPriority, number>>;
    section_percentages?: Record<string, string | number>;
    overall_seo_percentage?: number;
}

export interface SeoHealthData {
    date?: string;
    website_url?: string;
    seo_report?: SeoReport;
}

const SEO_REPORT_VIEW_ENDPOINT = '/master/seoReport/view';
const SEO_HEALTH_CHECK_UPSERT_ENDPOINT = '/master/seoHealthCheck/upsert';

const noRecordMessagePattern = /no\s+record\s+found/i;

export const isNoRecordFound = (value: any): boolean => {
    if (!value) {
        return false;
    }

    if (typeof value === 'string') {
        return noRecordMessagePattern.test(value);
    }

    return [
        value?.message,
        value?.detail,
        value?.data,
        value?.response?.data?.message,
        value?.response?.data?.detail,
        value?.response?.data?.data,
    ].some(isNoRecordFound);
};

const unwrapSeoResponseData = (responseData: any) => {
    const data = responseData?.data ?? responseData;

    if (Array.isArray(data)) {
        return data[0];
    }

    return data;
};

export const normalizeSeoHealthData = (data: any): SeoHealthData | null => {
    const unwrappedData = unwrapSeoResponseData(data);

    if (!unwrappedData || isNoRecordFound(unwrappedData)) {
        return null;
    }

    const nestedReport = unwrappedData?.seo_report || unwrappedData?.SEO_REPORT || unwrappedData?.report || unwrappedData?.REPORT;
    const hasTopLevelReport = Boolean(
        unwrappedData?.section_percentages
        || unwrappedData?.SECTION_PERCENTAGES
        || unwrappedData?.optimization_plan
        || unwrappedData?.OPTIMIZATION_PLAN
        || unwrappedData?.total_issues !== undefined
        || unwrappedData?.TOTAL_ISSUES !== undefined
        || unwrappedData?.overall_seo_percentage !== undefined
        || unwrappedData?.OVERALL_SEO_PERCENTAGE !== undefined,
    );
    const report = nestedReport || (
        hasTopLevelReport
            ? {
                url: unwrappedData?.url ?? unwrappedData?.URL ?? unwrappedData?.website_url ?? unwrappedData?.WEBSITE_URL,
                timestamp: unwrappedData?.timestamp ?? unwrappedData?.TIMESTAMP ?? unwrappedData?.time_stamp ?? unwrappedData?.TIME_STAMP,
                time_stamp: unwrappedData?.time_stamp ?? unwrappedData?.TIME_STAMP,
                total_issues: unwrappedData?.total_issues ?? unwrappedData?.TOTAL_ISSUES,
                optimization_plan: unwrappedData?.optimization_plan ?? unwrappedData?.OPTIMIZATION_PLAN,
                issues_by_priority: unwrappedData?.issues_by_priority ?? unwrappedData?.ISSUES_BY_PRIORITY,
                section_percentages: unwrappedData?.section_percentages ?? unwrappedData?.SECTION_PERCENTAGES,
                overall_seo_percentage: unwrappedData?.overall_seo_percentage ?? unwrappedData?.OVERALL_SEO_PERCENTAGE,
            }
            : undefined
    );

    return {
        date: unwrappedData?.date ?? unwrappedData?.DATE ?? unwrappedData?.updated_at ?? unwrappedData?.UPDATED_AT ?? unwrappedData?.created_at ?? unwrappedData?.CREATED_AT ?? report?.timestamp ?? report?.time_stamp,
        website_url: unwrappedData?.website_url ?? unwrappedData?.WEBSITE_URL ?? unwrappedData?.url ?? unwrappedData?.URL ?? report?.url,
        seo_report: report,
    };
};

export const buildWebsiteUrlPayload = (value?: string) => {
    if (!value) {
        return '';
    }

    return value.startsWith('http') ? value : `https://${value}`;
};

export const seoApi = {
    async viewReport() {
        const response = await httpclient().get(SEO_REPORT_VIEW_ENDPOINT);
        return normalizeSeoHealthData(response?.data);
    },

    async analyse(url: string) {
        const response = await httpclient().post(SEO_HEALTH_CHECK_UPSERT_ENDPOINT, {
            URL: url,
        });
        return normalizeSeoHealthData(response?.data);
    },
};
